import { useMemo } from 'react'
import { slugify } from '@/utils/strings'
import ComparisonGrid, { type TableData, type TableRowData } from '@/components/ui/comparison-grid'
import RichText from '@/components/ui/rich-text'
import type { TypeComponentComparisonTable, TypeTableRowSectionHeading, TypeTableRow } from '@/types/ctf'

type ComparisonTableProps = TypeComponentComparisonTable<'WITHOUT_UNRESOLVABLE_LINKS'>['fields'] & {
  isPrintView?: boolean
}

export default function ComparisonTable({
  anchorLinkTitle,
  isPrintView,
  internalName,
  eyebrowText,
  title,
  subtext,
  actions,
  featuresHeading,
  headerCells,
  rows,
  footer,
  textAlign = 'Center',
  layout = 'Default',
}: ComparisonTableProps) {
  const data: TableData = useMemo(
    () => ({
      anchorLinkTitle: anchorLinkTitle,
      id: internalName ? slugify(internalName) : undefined,
      tableTitle: title ?? '',
      tableEyebrowText: eyebrowText,
      subtext: subtext?.data ? <RichText body={subtext as any} /> : undefined,
      actions: actions,
      headers: [
        featuresHeading ? <RichText key="features" body={featuresHeading} /> : undefined,
        ...(headerCells || []).map((cell) => <RichText key={cell?.sys.id} body={cell?.fields.content as any} />),
      ],
      body: (rows || []).map((row) => {
        switch (row?.sys.contentType.sys.id) {
          case 'tableRow':
            const rowData = row?.fields as TypeTableRow<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']
            return {
              Tooltip: rowData.tooltip ? <RichText body={rowData.tooltip} /> : undefined,
              cells: [
                <RichText key={row.sys.id} body={rowData.title} />,
                ...(rowData?.cells?.map((cell) => <RichText key={cell?.sys.id} body={cell?.fields.content} />) || []),
              ],
            }
          case 'tableRowSectionHeading':
            const sectionHeadingData = row?.fields as TypeTableRowSectionHeading<'WITHOUT_UNRESOLVABLE_LINKS'>['fields']
            return {
              subheader: sectionHeadingData.title,
            }
          default:
            return {}
        }
      }) as TableRowData[],
      footer: footer
        ? [
            {
              Tooltip: footer.fields.tooltip ? <RichText body={footer.fields.tooltip} /> : undefined,
              cells: [
                <RichText key={footer.sys.id} body={footer.fields.title} />,
                ...(footer.fields.cells?.map((cell) => <RichText key={cell?.sys.id} body={cell?.fields.content} />) ||
                  []),
              ],
            },
          ]
        : undefined,
    }),
    [title, featuresHeading, headerCells, rows]
  )

  return <ComparisonGrid data={{ content: data }} textAlign={textAlign} layout={layout} isPrintView={isPrintView} />
}
